import React, { useState } from 'react';
import { storeService } from '../../services/store.service';
import { storageService } from '../../services/storage.service';
import AuthPendingOverlay from '../AuthPendingOverlay/AuthPendingOverlay';
import { API_BASE_URL, CMS_BASE_URL } from '../../config/api.config';

export interface UserProfile {
  name: string;
  email: string;
  picture: string;
}

interface GoogleLoginProps {
  currentStep?: string;
  children?: React.ReactNode;
  className?: string;
}

/**
 * Generates the OAuth state parameter based on environment.
 * Format: staging_{companyId} for staging, prod_{companyId} for production.
 */
const getAuthState = async (): Promise<string> => {
  // This storefront's business: the CMS sends the customer back to that
  // business's site and signs them in there (was hard-coded '1').
  const companyId = await storeService.getCompanyId();
  // Detect environment based on API URL
  const isStaging = API_BASE_URL.includes('apistg.');
  const envPrefix = isStaging ? 'staging' : 'prod';
  return `${envPrefix}_${companyId}`;
};

const GoogleLoginButton: React.FC<GoogleLoginProps> = ({ currentStep, children, className = "menu-button" }) => {
  /*
   * Between the click and Google's page appearing there is a gap — building
   * the URL, then the browser navigating away — during which nothing visibly
   * changes. Customers read that as a dead button and click again; a second
   * click mid-navigation just repeats the redirect. Hold a pending state so
   * the button says what is happening and cannot be pressed twice.
   */
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleGoogleLogin = async () => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    try {
      // Store the current page/step before redirecting to login
      if (currentStep) {
        storageService.setItem('pre_login_step', currentStep);
        console.log('Stored pre-login step:', currentStep);
      }
      
      const redirectUri = `${CMS_BASE_URL}/api/google-auth/callback`;
      const authState = await getAuthState();
      
      const params = new URLSearchParams({
        client_id: '435419771559-v495gdfgv86njb40n7dpp97mrrd7nt8b.apps.googleusercontent.com',
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid email profile',
        access_type: 'offline',
        prompt: 'consent',
        state: authState
      });

      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
      console.log('Starting Google OAuth:', redirectUri, 'with state:', authState);
      window.location.href = googleAuthUrl;
    } catch (error) {
      console.error('Error initiating Google OAuth:', error);
      // Nothing was navigated to, so give the button back rather than
      // leaving a permanently disabled control on screen.
      setIsRedirecting(false);
    }
  };

  return (
    <>
      <button
        onClick={handleGoogleLogin}
        className={className}
        disabled={isRedirecting}
        aria-busy={isRedirecting}
      >
        {children}
      </button>
      {/* The redirect can take a moment on a slow connection; the overlay
          covers that gap and blocks a second attempt at the same flow. */}
      <AuthPendingOverlay
        isVisible={isRedirecting}
        message="Taking you to Google…"
        hint="You'll come straight back after signing in."
      />
    </>
  );
};

export default GoogleLoginButton; 